/* eslint-disable no-param-reassign */
import { createReducer } from '@reduxjs/toolkit';
// action types
import { genericSETypes } from 'redux-utils/types/index';
import { map, keys, each, pick, isEmpty } from 'lodash';

import grModel from 'models/granularityModel';

// initial reducer state
const initialState = {
  preferences: {
    filters: {},
    granularities: {}
  },
  filters: {},
  filter: {},
  bottomUpFilter: {},
  // incremented on every user-initiated filter pick. a filter response is
  // applied only if the epoch is unchanged since its request was made.
  filterEpoch: 0,
  chartList: [],
  chartData: {},
  sessionData: {},
  reportData: {},
  apiRequestStatus: {
    chartData: {
      request: false,
      success: false,
      error: false,
      fetchedChart: []
    },
    chartList: { request: false, success: false, error: false },
    filters: { request: false, success: false, error: false },
    filter: { request: false, success: false, error: false },
    bottomUpFilter: { request: false, success: false, error: false },
    // per-leg status for the three session-data APIs. `failedLeg` names the
    // failing endpoint so the UI can say which one broke without leaking any
    // server detail.
    splitSessionData: {
      isRequestMade: false,
      failedLeg: null,
      images: { request: false, success: false, error: false },
      misc: { request: false, success: false, error: false },
      variants: { request: false, success: false, error: false }
    },
    reportData: { request: false, success: false, error: false },
    sessionFilterValuesStatus: {
      request: false,
      success: false,
      error: false,
      message: ''
    }
  },
  error: {
    state: false,
    type: ''
  },
  sessionFilters: null,
  sessionFilterValues: {}
};

/**
 * The shape ShelfVisualizer requires before any leg has landed.
 *
 * It reads `sessionData.variants` and `sessionData.images` without optional
 * chaining on the array itself (main.js:280, 304, 612, 665, 680) and throws on
 * `undefined` while tolerating `[]`. In progressive mode the component mounts
 * once images+misc are in, which fires the store_explorer_settings effect at
 * main.js:271 while the variants leg is still on the wire - so every key has to
 * exist from the moment the fetch starts, not from when its own leg resolves.
 */
const EMPTY_SESSION_DATA = {
  images: [],
  variants: [],
  store_explorer_settings: {},
  color_legend: {},
  session_data: [],
  session_review_status: [],
  columns_meta: {},
  excel_report: false,
  psa_file: null,
  latest_tse_correction_time: null
};

/**
 * @description Builds request/success/failure/stale handlers for one split
 * session-data leg. `pick` maps the leg's response onto the slice of the
 * assembled monolith-shaped `sessionData` that this leg owns.
 * @param {string} leg - 'images' | 'misc' | 'variants'
 * @param {object} actionGroup - the leg's action type group
 * @param {Function} pick - response -> partial sessionData
 * @returns {object} handler map to spread into createReducer
 */
const splitLegHandlers = (leg, actionGroup, pick) => ({
  [actionGroup.request]: state => {
    // Fill in any key not already present. All three legs dispatch in the same
    // tick before any response, so this is a merge rather than a reset - the
    // previous session is dropped by splitSessionDataClear on filter change.
    state.sessionData = { ...EMPTY_SESSION_DATA, ...state.sessionData };
    state.apiRequestStatus.splitSessionData.isRequestMade = true;
    state.apiRequestStatus.splitSessionData.failedLeg = null;
    state.apiRequestStatus.splitSessionData[leg] = {
      request: true,
      success: false,
      error: false
    };
  },
  [actionGroup.success]: (state, action) => {
    const { response } = action.payload;
    state.sessionData = {
      ...state.sessionData,
      ...pick(response.data || {})
    };
    state.apiRequestStatus.splitSessionData[leg] = {
      request: false,
      success: true,
      error: false
    };
  },
  [actionGroup.failure]: state => {
    // any leg failing fails the whole page, so drop the partial assembly
    // rather than render a half-populated session.
    state.sessionData = {};
    state.apiRequestStatus.splitSessionData.failedLeg = leg;
    state.apiRequestStatus.splitSessionData[leg] = {
      request: false,
      success: false,
      error: true
    };
  },
  // superseded by a newer filter selection - release the flag, write no data.
  [actionGroup.stale]: state => {
    state.apiRequestStatus.splitSessionData[leg] = {
      request: false,
      success: false,
      error: false
    };
  }
});

const reducer = createReducer(initialState, {
  [genericSETypes.setSessionFilters]: (state, action) => {
    state.sessionFilters = action.payload;
  },
  [genericSETypes.getChartData.request]: (state, action) => {
    state.chartData = {};
    state.apiRequestStatus.chartData = {
      request: true,
      success: false,
      error: false,
      fetchedChart: [
        ...(state.apiRequestStatus.chartData?.fetchedChart || []),
        action.payload.params.chart_name
      ]
    };
  },
  [genericSETypes.sessionFilterValues.success]: (state, action) => {
    const {
      payload: { response }
    } = action;
    state.sessionFilterValues = response.data;
    state.apiRequestStatus.sessionFilterValuesStatus = {
      request: false,
      success: true,
      error: false
    };
  },
  [genericSETypes.sessionFilterValues.request]: state => {
    state.apiRequestStatus.sessionFilterValuesStatus = {
      request: true,
      success: false,
      error: false
    };
  },
  [genericSETypes.sessionFilterValues.error]: (state, action) => {
    state.apiRequestStatus.sessionFilterValuesStatus = {
      request: false,
      success: false,
      error: true,
      message: action.payload.error.error_message
        ? action.payload.error.error_message
        : 'Something went wrong'
    };
  },
  [genericSETypes.getChartData.success]: (state, action) => {
    const {
      payload: { response }
    } = action;
    state.isFetching = false;
    state.chartData = {
      ...state.chartData,
      ...response.data
    };
    state.apiRequestStatus.chartData = {
      ...state.apiRequestStatus.chartData,
      request: false,
      success: true,
      error: false
    };
  },
  [genericSETypes.getBottomUpFilter.set]: (state, action) => {
    state.bottomUpFilter = action.payload;
  },
  [genericSETypes.getBottomUpFilter.request]: state => {
    state.bottomUpFilter = {};
    state.apiRequestStatus.bottomUpFilter = {
      request: true,
      success: false,
      error: false
    };
    // state.bottomUpFilter.incomingFilter = '';
  },
  [genericSETypes.getBottomUpFilter.success]: (state, action) => {
    const hashedOptions = action.payload.response.options.reduce(
      (obj, item) => {
        return {
          ...obj,
          [item.value]: item
        };
      },
      {}
    );
    state.bottomUpFilter[action.payload.response.filter_type] = {
      ...action.payload.response,
      hashedOptions
    };
    state.bottomUpFilter.incomingFilter = action.payload.response.filter_type;
    state.apiRequestStatus.bottomUpFilter = {
      request: false,
      success: true,
      error: false
    };
  },
  [genericSETypes.getBottomUpFilter.failure]: state => {
    state.bottomUpFilter = {};
    state.apiRequestStatus.bottomUpFilter = {
      request: false,
      success: false,
      error: true
    };
  },
  // superseded response - release the loading flag, write no filter data.
  [genericSETypes.getBottomUpFilter.stale]: state => {
    state.apiRequestStatus.bottomUpFilter = {
      request: false,
      success: false,
      error: false
    };
  },
  [genericSETypes.getChartData.failure]: (state, action) => {
    const {
      payload: {
        params: { chart_name: chartName }
      }
    } = action;
    if (state.chartData[chartName]) {
      state.chartData = {
        ...state.chartData,
        [state.chartData[chartName]]: {
          ...state.chartData[chartName],
          datasets: []
        }
      };
    }
    state.apiRequestStatus.chartData = {
      request: false,
      success: false,
      error: true
    };
  },
  [genericSETypes.getChartList.clear]: state => {
    state.chartList = [];
    state.apiRequestStatus.chartList = {
      request: false,
      success: false,
      error: false
    };
    state.apiRequestStatus.chartData.fetchedChart = [];
  },
  [genericSETypes.getChartList.request]: state => {
    state.chartList = [];
    state.apiRequestStatus.chartList = {
      request: true,
      success: false,
      error: false
    };
  },

  [genericSETypes.getChartList.success]: (state, action) => {
    const {
      payload: {
        params: { route_id: routeID },
        response: { data: chartList }
      }
    } = action;
    state.chartList = chartList;
    if (!isEmpty(state.preferences.granularities[routeID])) {
      return state;
    }
    const grList = {};
    each(chartList, chart => {
      const chartObject = pick(chart, [
        'granularities',
        'name',
        'params',
        'filters'
      ]);
      const grType = {};
      each(chartObject.granularities, e => {
        const options = map(e.options, obj => ({ ...obj, isHidden: false }));
        grType[e.type] = { ...e, options, value: options[0].value };
      });
      grList[chartObject.name] = grType;
      each(chartObject.filters, e => {
        const pickFrom = e.pick_from
          ? e.pick_from
          : e.filter_id.includes('brand')
          ? 'fl_category'
          : e.filter_id.includes('fl_posm')
          ? 'fl_channel'
          : 'fl_brand';
        if (pickFrom === 'fl_channel') {
          grType[e.filter_id] = {
            ...e,
            value: state.preferences.filters[routeID][pickFrom][e.filter_id][0]
          };
        } else {
          grType[e.filter_id] = {
            ...e,
            value: state.preferences.filters[routeID][pickFrom][e.filter_id][0]
          };
        }
      });
      grList[chartObject.name] = grType;
    });
    state.preferences.granularities[routeID] = {
      ...grList
    };

    state.apiRequestStatus.chartList = {
      request: false,
      success: true,
      error: false
    };
  },
  [genericSETypes.getChartList.failure]: state => {
    state.chartList = [];
    state.apiRequestStatus.chartList = {
      request: false,
      success: false,
      error: true
    };
  },
  [genericSETypes.getChartList.clear]: state => {
    state.chartList = [];
    state.apiRequestStatus.chartList = {
      request: false,
      success: false,
      error: false
    };
    state.apiRequestStatus.chartData.fetchedChart = [];
  },
  [genericSETypes.getFilter.request]: state => {
    state.filter = {};
    state.apiRequestStatus.filter = {
      request: true,
      success: false,
      error: false
    };
    // state.bottomUpFilter.incomingFilter = '';
  },
  [genericSETypes.getFilter.success]: (state, action) => {
    const hashedOptions = action.payload.response.options.reduce(
      (obj, item) => {
        return {
          ...obj,
          [item.value]: item
        };
      },
      {}
    );
    state.bottomUpFilter[action.payload.response.filter_type] = {
      ...action.payload.response,
      hashedOptions
    };
    state.bottomUpFilter.incomingFilter = action.payload.response.filter_type;
    state.filter = action.payload.response;
    state.apiRequestStatus.filter = {
      request: false,
      success: true,
      error: false
    };
  },
  [genericSETypes.getFilter.failure]: state => {
    state.filter = {};
    state.apiRequestStatus.filter = {
      request: false,
      success: false,
      error: true
    };
  },
  // superseded response - release the loading flag, write no filter data.
  // state.filter is left as-is so the cascade does not re-render and the
  // stale chain stops here.
  [genericSETypes.getFilter.stale]: state => {
    state.apiRequestStatus.filter = {
      request: false,
      success: false,
      error: false
    };
  },
  [genericSETypes.bumpFilterEpoch]: state => {
    state.filterEpoch += 1;
  },
  [genericSETypes.getFilters.request]: state => {
    state.filters = {};
    state.apiRequestStatus.filters = {
      request: true,
      success: false,
      error: false
    };
  },
  [genericSETypes.getFilters.success]: (state, action) => {
    state.filters = action.payload.response.data;
    state.apiRequestStatus.filters = {
      request: false,
      success: true,
      error: false
    };
  },
  [genericSETypes.getFilters.failure]: state => {
    state.filters = {};
    state.apiRequestStatus.filters = {
      request: false,
      success: false,
      error: true
    };
  },
  // ---------------------------------------------------------------------------
  // session data, assembled from the three parallel legs
  //
  // The three legs land independently and merge into the same `sessionData`
  // object the monolith produces, so GenericSE/ShelfVisualizer read an identical
  // shape and need no changes. `images`/`variants` are always arrays and
  // `store_explorer_settings`/`color_legend` always objects - ShelfVisualizer
  // throws on `undefined` at main.js:280,304,612,615 but tolerates empty ones.
  // ---------------------------------------------------------------------------
  [genericSETypes.splitSessionDataClear]: state => {
    state.sessionData = {};
    state.apiRequestStatus.splitSessionData = {
      ...initialState.apiRequestStatus.splitSessionData
    };
  },
  ...splitLegHandlers('images', genericSETypes.getSessionDataImages, response => ({
    images: Array.isArray(response.images) ? response.images : []
  })),
  ...splitLegHandlers('variants', genericSETypes.getSessionDataVariants, response => ({
    variants: Array.isArray(response.variants) ? response.variants : []
  })),
  ...splitLegHandlers('misc', genericSETypes.getSessionDataMisc, response => ({
    excel_report: response.excel_report,
    columns_meta: response.columns_meta || {},
    store_explorer_settings: response.store_explorer_settings || {},
    session_data: Array.isArray(response.session_data)
      ? response.session_data
      : [],
    session_review_status: Array.isArray(response.session_review_status)
      ? response.session_review_status
      : [],
    latest_tse_correction_time: response.latest_tse_correction_time,
    psa_file: response.psa_file,
    // absent from the split contract and from every client response observed,
    // but ShelfVisualizer reads it - keep it an object so the legend no-ops.
    color_legend: response.color_legend || {}
  })),
  [genericSETypes.downloadChartData.request]: state => {
    state.reportData = {};
    state.apiRequestStatus.reportData = {
      request: true,
      success: false,
      error: false
    };
  },
  [genericSETypes.downloadChartData.success]: (state, action) => {
    state.reportData = typeof action.payload.response === 'string'? action.payload.response :action.payload.response.data;
    state.apiRequestStatus.reportData = {
      request: false,
      success: true,
      error: false
    };
  },
  [genericSETypes.downloadChartData.failure]: state => {
    state.reportData = {};
    state.apiRequestStatus.reportData = {
      request: false,
      success: false,
      error: true
    };
  },
  [genericSETypes.genericSEfilterChange]: (state, action) => {
    const {
      payload: { value: filterList, chartList }
    } = action;
    const routeID = action.payload.routeID || action.payload.routeIDParam;
    const { filterType, grType, valueKey } = grModel;
    const filterId = keys(filterList);
    if (
      filterId.includes(filterType) &&
      state.preferences.granularities[routeID] !== undefined
    ) {
      map(chartList, name => {
        map(
          state.preferences.granularities[routeID][name][grType].options,
          el => {
            el.isHidden = false;
            if (el.value === valueKey) {
              el.isHidden = filterList.fl_type.value !== 'shelf';
            }
            return { ...el };
          }
        );
        state.preferences.granularities[routeID][name] = {
          ...state.preferences.granularities[routeID][name],
          [grType]: {
            ...state.preferences.granularities[routeID][name][grType],
            value: filterList.fl_type.value === 'shelf' ? 'brand' : 'brand-form'
          }
        };
      });
    }
    state.activeRouteID = routeID;
    const filters = {
      ...state.preferences.filters
    };
    filters[routeID] = {
      ...filters[routeID],
      ...filterList
    };
    // if (filterList.fl_time) {
    //   map(filters, (fl, route) => {
    //     filters[route] = {
    //       ...filters[route],
    //       fl_time: { ...filterList.fl_time }
    //     };
    //   });
    // }

    state.preferences.filters = { ...filters };
  }
});

export default reducer;
